import React from 'react';

import { FormattedMessage } from 'react-intl';

import type {
	ExtensionManifest,
	ExtensionModule,
	MaybeADFEntity,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import { AssetsAttributionIcon } from '@atlaskit/editor-common/quick-insert/assets-attribution-icon';
import { ConfluenceAttributionIcon } from '@atlaskit/editor-common/quick-insert/confluence-attribution-icon';
import { FeedbackAttributionIcon } from '@atlaskit/editor-common/quick-insert/feedback-attribution-icon';
import { FocusAttributionIcon } from '@atlaskit/editor-common/quick-insert/focus-attribution-icon';
import { JiraAttributionIcon } from '@atlaskit/editor-common/quick-insert/jira-attribution-icon';
import { JiraServiceManagementAttributionIcon } from '@atlaskit/editor-common/quick-insert/jira-service-management-attribution-icon';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import type { QuickInsertPreview } from '@atlaskit/editor-common/quick-insert/preview';
import { StudioAttributionIcon } from '@atlaskit/editor-common/quick-insert/studio-attribution-icon';
import { TalentAttributionIcon } from '@atlaskit/editor-common/quick-insert/talent-attribution-icon';
import { TrelloAttributionIcon } from '@atlaskit/editor-common/quick-insert/trello-attribution-icon';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Text } from '@atlaskit/primitives/compiled/text';
import {
	ROVO_SKILL_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import type { Skill } from '@atlassian/rovo-platform-types/skills';
import { isCustomSkill } from '@atlassian/rovo-platform-ui-components/skills/is-custom-skill';
import { resolveSkillDisplayDescription } from '@atlassian/rovo-platform-ui-components/skills/resolve-skill-display-description';
import { getIconColor } from '@atlassian/rovo-platform-ui-components/skills/skill-colors';
import {
	defaultIcon,
	skillIcon,
	type SkillIconName,
} from '@atlassian/rovo-platform-ui-components/skills/skill-icons';

const DEFAULT_CATEGORY = 'AI';
const MAX_DESCRIPTION_LENGTH = 150;
const PRODUCT_ATTRIBUTIONS: Record<string, NonNullable<QuickInsertPreview['attribution']>> = {
	confluence: {
		name: quickInsertMessages.previewAttributionConfluence,
		icon: ConfluenceAttributionIcon,
	},
	jira: { name: quickInsertMessages.previewAttributionJira, icon: JiraAttributionIcon },
	jsm: {
		name: quickInsertMessages.previewAttributionJiraServiceManagement,
		icon: JiraServiceManagementAttributionIcon,
	},
	talent: { name: quickInsertMessages.previewAttributionTalent, icon: TalentAttributionIcon },
	studio: { name: quickInsertMessages.previewAttributionStudio, icon: StudioAttributionIcon },
	assets: { name: quickInsertMessages.previewAttributionAssets, icon: AssetsAttributionIcon },
	focus: { name: quickInsertMessages.previewAttributionFocus, icon: FocusAttributionIcon },
	trello: { name: quickInsertMessages.previewAttributionTrello, icon: TrelloAttributionIcon },
	feedback: { name: quickInsertMessages.previewAttributionFeedback, icon: FeedbackAttributionIcon },
};
// Atlassian skills whose product is not in PRODUCT_ATTRIBUTIONS, or that have no product, are
// attributed to Atlassian.
const DEFAULT_ATTRIBUTION: NonNullable<QuickInsertPreview['attribution']> = {
	name: quickInsertMessages.previewAttributionAtlassian,
};

// Custom skills are authored by users rather than by a product, so they have no attribution.
const getSkillAttribution = (skill: Skill): QuickInsertPreview['attribution'] => {
	if (isCustomSkill(skill.id)) {
		return undefined;
	}
	return (skill.productKey && PRODUCT_ATTRIBUTIONS[skill.productKey]) || DEFAULT_ATTRIBUTION;
};

const DEFAULT_BETA_LOZENGE = (
	<Text color="color.text.subtle" size="small">
		<FormattedMessage
			description="Label indicating that a Skill is in beta"
			defaultMessage="Beta"
			id="fabric.editor.quickInsert.skill.beta"
		/>
	</Text>
);

/**
 * Truncate description to a maximum length.
 * Used to keep insert menu items readable when skill descriptions are long.
 */
function truncateDescription(description: string): string {
	if (description.length <= MAX_DESCRIPTION_LENGTH) {
		return description;
	}
	return description.slice(0, MAX_DESCRIPTION_LENGTH).trim() + '…';
}

type SkillIconProps = {
	color?: string;
	label?: string;
};

/**
 * Returns the skill-specific icon with its configured color.
 * Falls back to the default skill icon if no match is found.
 */
const getLegacySkillIcon = (skill: Skill) => {
	const iconName = skill.iconName as SkillIconName | undefined;
	const loadIcon = iconName && skillIcon[iconName] ? skillIcon[iconName] : defaultIcon;
	const iconColor = getIconColor(skill.color);

	return async () => {
		const { default: Icon } = await loadIcon();
		const IconComponent = Icon as React.ComponentType<SkillIconProps>;
		const ColoredSkillIcon = (props: SkillIconProps) => (
			<IconComponent {...props} color={iconColor} />
		);

		return { default: ColoredSkillIcon };
	};
};

/**
 * Options for customizing how skills are rendered in the manifest.
 * Both product editors and Rovo Chat can use this to configure the manifest.
 */
export type BuildLegacySkillsManifestOptions = {
	isHidden?: (skill: Skill) => boolean;
	/**
	 * Called when a skill is selected. Return value is passed back to the editor.
	 * - Product editors: call `onSkillSelected` then return void
	 * - Rovo Chat: return `createSkillNode(skill)` to insert the ADF node
	 */
	onAction?: (skill: Skill) => MaybeADFEntity | Promise<MaybeADFEntity>;

	/**
	 * Categories for the quick insert items.
	 * @default ['AI']
	 */
	categories?: string[];

	/**
	 * Priority for quick insert items (lower = higher priority).
	 * @default undefined (uses editor default of 1)
	 */
	priority?: number;

	/**
	 * Custom lozenge renderer per skill (e.g. Beta badge).
	 * When omitted, beta skills receive the default plain Beta label. Return undefined to suppress it.
	 */
	getLozenge?: (skill: Skill) => React.ReactNode | undefined;

	/**
	 * Custom node renderers for the extension (e.g. SkillTag chip rendering).
	 * If provided, added to `modules.nodes` in the manifest.
	 */
	nodes?: ExtensionManifest['modules']['nodes'];
};

/**
 * Builds an ExtensionManifest from a list of skills.
 *
 * This shared function builds the skill modules used by product editors and Rovo Chat.
 * It uses the same `key` and `type` constants so that `inlineExtension` nodes
 * render correctly across both contexts.
 *
 * @param skills - The skills to include in the manifest
 * @param options - Optional configuration for customizing the manifest
 *
 * @example
 * ```typescript
 * // Product editor usage:
 * buildLegacySkillsManifest(skills, {
 *   onAction: (skill) => { openInlinePrompt(skill); },
 * });
 *
 * // Rovo Chat usage:
 * buildLegacySkillsManifest(skills, {
 *   onAction: (skill) => createSkillNode(skill),
 *   categories: ['Skills'],
 *   priority: 99,
 *   getLozenge: (skill) => skill.tags?.includes('beta') ? <BetaLozenge /> : undefined,
 *   nodes: { skillTag: { type: 'inlineExtension', render: ... } },
 * });
 * ```
 */
export function buildLegacySkillsManifest(
	skills: Skill[],
	options: BuildLegacySkillsManifestOptions = {},
): ExtensionManifest {
	const {
		onAction,
		categories = [DEFAULT_CATEGORY],
		priority,
		getLozenge,
		nodes,
		isHidden,
	} = options;
	const showSlashPreviews = isExperimentEnabled('platform_editor_slash_command');

	const quickInsertModules = skills.map((skill) => {
		const keywords: string[] = [skill.slug, skill.product, skill.name, 'skill', 'skills'].filter(
			(k): k is string => typeof k === 'string' && k.length > 0,
		);

		const description = resolveSkillDisplayDescription(skill.helpText, skill.description);
		let preview: QuickInsertPreview | undefined;
		if (showSlashPreviews) {
			const attribution = skill.productKey ? PRODUCT_ATTRIBUTIONS[skill.productKey] : undefined;
			preview = attribution
				? { attribution }
				: skill.product
					? { attribution: { name: skill.product } }
					: {};
		}

		return {
			key: skill.slug,
			...(isHidden && { isHidden: () => isHidden(skill) }),
			title: skill.name,
			description: showSlashPreviews ? description : truncateDescription(description),
			keywords,
			categories,
			...(priority !== undefined && { priority }),
			icon: getLegacySkillIcon(skill),
			...(getLozenge && { lozenge: getLozenge(skill) }),
			...(preview && { preview }),
			action: async (): Promise<MaybeADFEntity> => onAction?.(skill),
		};
	});

	return {
		title: 'Rovo Skills',
		description: 'AI-powered skills from Rovo',
		type: ROVO_SKILL_EXTENSION_TYPE,
		key: ROVO_SKILL_EXTENSION_KEY,
		icons: {
			'16': defaultIcon,
			'24': defaultIcon,
			'48': defaultIcon,
		},
		modules: {
			quickInsert: quickInsertModules,
			...(nodes && { nodes }),
		},
	};
}

/**
 * Returns the skill-specific icon with its configured color.
 * Falls back to the default skill icon if no match is found.
 */
export const getSkillIcon = (skill: Skill): NonNullable<ExtensionModule['icon']> => {
	const iconName = skill.iconName as SkillIconName | undefined;
	const loadIcon = iconName && skillIcon[iconName] ? skillIcon[iconName] : defaultIcon;
	const iconColor = getIconColor(skill.color);

	const loadColoredIcon = async () => {
		const { default: Icon } = await loadIcon();
		const IconComponent = Icon as React.ComponentType<SkillIconProps>;
		const ColoredSkillIcon = (props: SkillIconProps) => (
			<IconComponent {...props} color={iconColor} />
		);

		return { default: ColoredSkillIcon };
	};
	let coloredIconPromise: ReturnType<typeof loadColoredIcon> | undefined;
	return () => (coloredIconPromise ??= loadColoredIcon());
};

/**
 * Options for customizing how skills are rendered in the manifest.
 * Both product editors and Rovo Chat can use this to configure the manifest.
 */
export type BuildSkillsManifestOptions = {
	isHidden?: (skill: Skill) => boolean;
	/**
	 * Called when a skill is selected. Return value is passed back to the editor.
	 * - Product editors: call `onSkillSelected` then return void
	 * - Rovo Chat: return `createSkillNode(skill)` to insert the ADF node
	 */
	onAction?: (skill: Skill) => MaybeADFEntity | Promise<MaybeADFEntity>;

	/**
	 * Categories for the quick insert items.
	 * @default ['AI']
	 */
	categories?: string[];

	/**
	 * Priority for quick insert items (lower = higher priority).
	 * @default undefined (uses editor default of 1)
	 */
	priority?: number;

	/**
	 * Custom lozenge renderer per skill (e.g. Beta badge).
	 * Return undefined to show no lozenge.
	 */
	getLozenge?: (skill: Skill) => React.ReactNode | undefined;

	/**
	 * Custom node renderers for the extension (e.g. SkillTag chip rendering).
	 * If provided, added to `modules.nodes` in the manifest.
	 */
	nodes?: ExtensionManifest<Skill>['modules']['nodes'];

	/** Optional manifest title override. */
	manifestTitle?: string;
};

/**
 * Builds an ExtensionManifest from a list of skills.
 *
 * This shared function builds the skill modules used by product editors and Rovo Chat.
 * It uses the same `key` and `type` constants so that `inlineExtension` nodes
 * render correctly across both contexts.
 *
 * @param skills - The skills to include in the manifest
 * @param options - Optional configuration for customizing the manifest
 *
 * @example
 * ```typescript
 * // Product editor usage:
 * buildSkillsManifest(skills, {
 *   onAction: (skill) => { openInlinePrompt(skill); },
 * });
 *
 * // Rovo Chat usage:
 * buildSkillsManifest(skills, {
 *   onAction: (skill) => createSkillNode(skill),
 *   categories: ['Skills'],
 *   priority: 99,
 *   getLozenge: (skill) => skill.tags?.includes('beta') ? <BetaLozenge /> : undefined,
 *   nodes: { skillTag: { type: 'inlineExtension', render: ... } },
 * });
 * ```
 */
export function buildSkillsManifest(
	skills: Skill[],
	options: BuildSkillsManifestOptions = {},
): ExtensionManifest<Skill> {
	const {
		onAction,
		categories = [DEFAULT_CATEGORY],
		priority,
		getLozenge,
		nodes,
		manifestTitle = 'Rovo Skills',
		isHidden,
	} = options;

	const quickInsertModules = skills.map((skill) => {
		const keywords: string[] = [skill.slug, skill.product, skill.name, 'skill', 'skills'].filter(
			(k): k is string => typeof k === 'string' && k.length > 0,
		);

		const description = resolveSkillDisplayDescription(skill.helpText, skill.description);
		const attribution = getSkillAttribution(skill);
		const preview: QuickInsertPreview = attribution ? { attribution } : {};
		const isBeta = skill.tags?.some((tag) => tag.toLowerCase() === 'beta') ?? false;
		const lozenge = getLozenge ? getLozenge(skill) : isBeta ? DEFAULT_BETA_LOZENGE : undefined;

		return {
			key: skill.slug,
			...(isHidden && { isHidden: () => isHidden(skill) }),
			title: skill.name,
			description,
			keywords,
			categories,
			...(priority !== undefined && { priority }),
			icon: getSkillIcon(skill),
			...(lozenge != null && { lozenge }),
			preview,
			// The inserted skill node stores the skill as its parameters.
			parameters: skill,
			action: async (): Promise<MaybeADFEntity> => onAction?.(skill),
		};
	});

	return {
		title: manifestTitle,
		description: 'AI-powered skills from Rovo',
		type: ROVO_SKILL_EXTENSION_TYPE,
		key: ROVO_SKILL_EXTENSION_KEY,
		icons: {
			'16': defaultIcon,
			'24': defaultIcon,
			'48': defaultIcon,
		},
		modules: {
			quickInsert: quickInsertModules,
			...(nodes && { nodes }),
		},
	};
}
